import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useCartStore from '../../store/cartStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountWishlist = () => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const addToCart = useCartStore(state => state.addItem);

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data.wishlist || []);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/account/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(item => item.id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const moveToCart = async (product) => {
    // Add to cart
    addToCart({
      product_id: product.id,
      product_name: product.name,
      price: product.discount_price || product.price,
      image: product.images?.[0] || '',
      size: product.sizes?.[0] || 'M',
      color: product.variants?.[0]?.color || 'Default',
      quantity: 1
    });
    
    // Remove from wishlist
    await removeFromWishlist(product.id);
    toast.success('Added to cart');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-wishlist">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-light text-charcoal">My Wishlist</h1>
        <span className="text-sm text-charcoal-light">{wishlist.length} items</span>
      </div>

      {wishlist.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {wishlist.map((product) => (
            <div key={product.id} className="bg-white border border-charcoal/10 group">
              {/* Image */}
              <div className="relative aspect-[3/4] overflow-hidden">
                <Link to={`/products/${product.id}`}>
                  <img
                    src={product.images?.[0] || ''}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </Link>
                
                {/* Discount Badge */}
                {product.discount_price && product.price > product.discount_price && (
                  <span className="absolute top-2 left-2 bg-gold text-white text-xs px-2 py-1">
                    {Math.round((1 - product.discount_price / product.price) * 100)}% OFF
                  </span>
                )}
                
                {/* Remove Button */}
                <button
                  onClick={() => removeFromWishlist(product.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-white shadow-md flex items-center justify-center hover:bg-red-50 transition-colors"
                >
                  <X className="w-4 h-4 text-charcoal-light hover:text-red-600" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-4">
                <Link to={`/products/${product.id}`}>
                  <h3 className="text-sm text-charcoal font-medium line-clamp-2 hover:text-gold transition-colors">
                    {product.name}
                  </h3>
                </Link>
                
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-heading text-lg text-gold">
                    {formatCurrency(product.discount_price || product.price)}
                  </span>
                  {product.discount_price && product.price > product.discount_price && (
                    <span className="text-sm text-charcoal-light line-through">
                      {formatCurrency(product.price)}
                    </span>
                  )}
                </div>
                
                {/* Add to Cart Button */}
                <button
                  onClick={() => moveToCart(product)}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2 bg-gold text-white text-sm font-medium hover:bg-gold-light transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Move to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <Heart className="w-16 h-16 mx-auto text-charcoal-light mb-4" />
          <h3 className="font-heading text-xl font-light text-charcoal mb-2">Your wishlist is empty</h3>
          <p className="text-charcoal-light mb-4">Save items you love to your wishlist</p>
          <Link
            to="/products"
            className="inline-block bg-gold text-white px-6 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
          >
            Browse Products
          </Link>
        </div>
      )}
    </div>
  );
};

export default AccountWishlist;
